// Each rider saved as the guest goes (the owner, 25 Sept: "Surely we can
// automatically save as we progress?"). The full-page checkout saves the
// rider on screen with the one-rider writer (save_booking_participant_row,
// 20260927160010 + 20260927160510) when the guest taps Next, Back or another
// rider's chip, instead of the whole list once at the end of the Riders step,
// so a closed tab or a lost signal loses at most the rider being typed. The
// drawer's one-rider pager (a phone) does the same with the same rules and
// words (leaveBeforeSave / leaveAfterSave); the desktop drawer, every rider
// on screen at once, still saves the whole list on Proceed.
//
// This module is the state around those saves: which riders the server holds
// as the page shows them (the tick), what `expected` each save sends, what a
// reply does to the forms, the words for every refusal, and where a restored
// checkout picks up. Pure: no React, no network, no storage, no clock.
// Pinned by e2e/rider-autosave-logic.spec.ts.
//
// Rules it keeps:
//   * I1: a rider is stored whole or not at all. A rider the checkout's own
//     rules call incomplete is never sent and never ticked; the server
//     refuses a partial row too (invalid_participant_details).
//   * Never overwrite: every save sends the rider as this page last read it
//     (`expected`); another save in between is refused rider_changed with
//     nothing stored, and the page then shows THAT rider as the server holds
//     it now, and says so. A read of the forms that finds a rider changed
//     under the guest's unsaved typing does the same.
//   * The page's own save is never taken for someone else's: a save whose
//     reply was lost (the signal, or the page's time limit) may have been
//     stored. When the next save of that rider is refused rider_changed and
//     the rider the server holds now is one this page sent (as the reader
//     returns it), it was this page's save: the rider is in as sent, and the
//     card, if the guest changed it since, is sent again (landedOwnSave).
//     When two such saves differ only in private answers (which the reader
//     never returns), which one landed is not known: the card is sent
//     again, never ticked on a guess (landedOwnSaveOf).
//   * A rider put on a card from the server after a conflict is marked
//     adopted (CheckoutDraft.adopted): typing someone else over it asks the
//     private questions again instead of keeping the stored person's.
//   * Nothing here stores an answer anywhere: the marks live in the page's
//     memory beside the draft, and a restored checkout learns which riders
//     are in from the server's reader, never from sessionStorage.
//   * BY RIDER ID (20261001110020): every rider the server holds has an id
//     (`_pid`). A save sends the rider with the id the page holds for it (a
//     reader's row, or the id the previous save's reply named, adopted onto
//     the draft row that was sent: adoptSavedIds by its _key). A rider with
//     no id yet is a new rider and fills the slot nobody is in (the server
//     gives it that slot's id). The server never takes a row for a rider by
//     its place: a stale page is refused (participant_unknown / _missing /
//     _order_changed) and reads the forms again. A slot nobody is in yet is
//     a placeholder ({_pid}, no answer): the page reads it as nobody, like
//     {} (riderSeen), so the padding a save adds never looks like a change.
//   * The tick means the server holds this rider's answers as this page
//     sent or read them. A PRIVATE answer changed in another window is not a
//     conflict on the server (20260927160510), and the reader never returns
//     one, so the page cannot see such a change; the payment gate judges.

import {
  PARTICIPANT_ID_KEY,
  SELF_KEY,
  isParticipantIdRefusal,
  isPlaceholderRow,
  participantIdOf,
  storedRowsCounted,
  type AnswerRow,
  type GuestNoun,
} from "@vertu/shared";
import { LAPSE_MESSAGE } from "./hold-timer-logic";
import {
  markAdopted,
  nounTitle,
  riderRowPayload,
  withoutAdopted,
  type CheckoutDraft,
  type CheckoutForm,
  type CheckoutVisit,
  type DraftRow,
} from "./booking-questions-form";
import { riderIssue, riderSlots, slotForm, type RiderSlot } from "./checkout-steps";

// ─── Which riders are in ────────────────────────────────────────────────────

/**
 * Per rider card (RiderSlot.key): the fingerprint of the row the server holds
 * for that rider, as this page last SENT it (an accepted save) or READ it (a
 * rider the reader returned as in). A rider is saved while what the page
 * would send now is exactly that row. Memory only: never stored.
 */
export type SavedRiders = Readonly<Record<string, string>>;

export const NO_SAVED_RIDERS: SavedRiders = {};

function sorted(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sorted);
  if (v && typeof v === "object")
    return Object.fromEntries(
      Object.keys(v as Record<string, unknown>)
        .sort()
        .map((k) => [k, sorted((v as Record<string, unknown>)[k])]),
    );
  return v;
}

/** One row as a stable string (keys in order), to tell whether two rows hold the same answers. */
export function rowFingerprint(row: unknown): string {
  return JSON.stringify(sorted(row ?? {}));
}

/** What a save of this rider would send now, or null (the forms changed under the card). */
export function riderPayloadOf(
  visits: readonly CheckoutVisit[],
  draft: CheckoutDraft,
  slot: RiderSlot,
): AnswerRow | null {
  const found = slotForm(visits, slot);
  return found ? riderRowPayload(found.form, draft, slot.ordinal) : null;
}

/**
 * Is this rider saved as the card shows it? The server holds exactly what a
 * save would send now, and the rider is complete. A rider edited since it
 * was saved is not (until the next save), and neither is one the checkout's
 * rules call incomplete: a partly filled rider is never ticked.
 */
export function isRiderSaved(
  visits: readonly CheckoutVisit[],
  draft: CheckoutDraft,
  saved: SavedRiders,
  slot: RiderSlot,
): boolean {
  const mark = saved[slot.key];
  if (!mark) return false;
  const payload = riderPayloadOf(visits, draft, slot);
  return payload !== null && mark === rowFingerprint(payload) && riderIssue(visits, draft, slot) === null;
}

/** isRiderSaved for every rider card, in order. */
export function savedFlags(
  visits: readonly CheckoutVisit[],
  draft: CheckoutDraft,
  saved: SavedRiders,
  slots: readonly RiderSlot[] = riderSlots(visits),
): boolean[] {
  return slots.map((slot) => isRiderSaved(visits, draft, saved, slot));
}

/** The first rider not saved yet, or -1 (every rider is in). */
export function firstUnsavedRider(
  visits: readonly CheckoutVisit[],
  draft: CheckoutDraft,
  saved: SavedRiders,
  slots: readonly RiderSlot[] = riderSlots(visits),
): number {
  return savedFlags(visits, draft, saved, slots).indexOf(false);
}

/**
 * Where the Riders step opens after a reload or the trip to Paystack: the
 * first rider not saved yet; when every rider is in, where the guest was
 * (clamped to the riders there are). (Continue on Your details goes past
 * the Riders step when every rider is in: nothingLeftToSave.)
 */
export function resumeRiderIndex(flags: readonly boolean[], was: number): number {
  const first = flags.indexOf(false);
  if (first >= 0) return first;
  return flags.length === 0 ? 0 : Math.min(Math.max(0, Math.floor(was) || 0), flags.length - 1);
}

/** Every rider is saved as shown (and there is at least one): the Riders step has nothing left to do. */
export function nothingLeftToSave(flags: readonly boolean[]): boolean {
  return flags.length > 0 && flags.every(Boolean);
}

/**
 * How long a one-rider save may take before the page stops waiting and
 * treats it as the network (the answers stay on the card, the next Next
 * tries again). The request may still land: landedOwnSave recognises it.
 */
export const RIDER_SAVE_TIMEOUT_MS = 8000;

function holdsAnswer(row: Readonly<Record<string, unknown>>): boolean {
  return Object.entries(row).some(([key, value]) => key !== "_pid" && value !== null && value !== undefined);
}

/** The row clears an answer: an explicit null (a kept private answer the guest cleared, an adopted rider's). */
function clearsAnswer(row: Readonly<Record<string, unknown>>): boolean {
  return Object.entries(row).some(([key, value]) => key !== "_pid" && value === null);
}

/**
 * Nothing to send for this rider: nothing typed on the card (every question
 * it shows is optional and left empty), nothing cleared on it, and nothing
 * stored for it (`expected` {}). The writer refuses an empty row (empty_row)
 * and there is nothing to keep: the rider stays {} (whole or nothing, I1)
 * and is never sent. A clear is always sent (an explicit null: the guest
 * asked for a kept private answer to go; 20260928150010 stores it even when
 * it leaves the rider with no answer, and nothingHeldToClear reads the
 * refusal when there was nothing there), and so is a rider the server holds
 * answers for, whatever the card shows.
 */
export function nothingToSend(payload: Readonly<Record<string, unknown>>, expected: Readonly<Record<string, unknown>>): boolean {
  return !holdsAnswer(payload) && !clearsAnswer(payload) && !holdsAnswer(expected);
}

/**
 * empty_row for a row with no answer (a clear, or an all-optional card
 * emptied): the writer stores such a row only in place of a rider who holds
 * answers (20260928150010), so the server holds none for this rider and
 * there is nothing to clear. The rider is already as the card shows it: in.
 */
export function nothingHeldToClear(reason: unknown, sent: Readonly<Record<string, unknown>>): boolean {
  return reason === "empty_row" && !holdsAnswer(sent);
}

/** nothingToSend for this rider card as the forms and the draft stand. */
export function riderHasNothingToSend(visits: readonly CheckoutVisit[], draft: CheckoutDraft, slot: RiderSlot): boolean {
  const found = slotForm(visits, slot);
  return !!found && nothingToSend(riderRowPayload(found.form, draft, slot.ordinal), expectedRow(found.form, slot.ordinal));
}

/**
 * The riders the end of the Riders step, and Pay, still have to send: every
 * complete rider not saved yet, in order. (A rider still missing something
 * is not here: the page takes the guest to it instead, and never sends it;
 * nor is a rider with nothing to send, nothingToSend.)
 */
export function ridersToSave(
  visits: readonly CheckoutVisit[],
  draft: CheckoutDraft,
  saved: SavedRiders,
  slots: readonly RiderSlot[] = riderSlots(visits),
): number[] {
  const out: number[] = [];
  slots.forEach((slot, index) => {
    if (
      riderIssue(visits, draft, slot) === null &&
      !isRiderSaved(visits, draft, saved, slot) &&
      !riderHasNothingToSend(visits, draft, slot)
    )
      out.push(index);
  });
  return out;
}

// ─── What a save sends, and what its reply does ─────────────────────────────

/**
 * `expected` for a save of this rider: the rider exactly as this page last
 * read it — the reader's row at this place, or the `row` the previous save's
 * reply carried — and {} for a place the reader returned nothing for.
 * Untouched: the server compares it with what its reader returns now.
 */
export function expectedRow(form: CheckoutForm, ordinal: number): Record<string, unknown> {
  const row = form.participants[ordinal];
  return row && typeof row === "object" && !Array.isArray(row) ? (row as Record<string, unknown>) : {};
}

/**
 * The form after the server accepted one rider: the list as the server now
 * holds it (padded with {} up to the party, as the writer pads it; nothing
 * beyond the party, which the writer trims), this rider as the reply's `row`
 * (the reader's view: private answers held back; the next save's
 * `expected`). `complete`: true once no rider is outstanding — every rider
 * has answers and the writer stores a rider only whole (I1), so a kept
 * private answer of a rider read earlier stays counted as kept — else null
 * ("not said since"): a rider not in yet must never count as holding
 * private answers. The payment gate on the server stays the judge; a refusal
 * there reads the forms again.
 */
export function formAfterRiderSaved(
  form: CheckoutForm,
  ordinal: number,
  row: unknown,
  outstanding: unknown,
): CheckoutForm {
  // A reply without them (never from 20260927160510 on) claims nothing: the
  // rider as {} (the next save is then refused rider_changed and reads the
  // forms again) and "not said" for the list.
  const stored = row && typeof row === "object" && !Array.isArray(row) ? (row as AnswerRow) : {};
  const participants = Array.from({ length: form.party_size }, (_, i) =>
    i === ordinal ? { ...stored } : ((form.participants[i] ?? {}) as AnswerRow),
  ) as AnswerRow[];
  const done = Array.isArray(outstanding) && outstanding.length === 0;
  return { ...form, participants, complete: done ? true : null };
}

/**
 * The draft after one rider was saved: "use my details" is no longer
 * offered once that rider is Rider 1 (a saved rider is never refilled from
 * the booker), and the rider is no longer an adopted one (the server holds
 * what the guest gave now). The rider's own answers stay as typed.
 */
export function draftAfterRiderSaved(draft: CheckoutDraft, bookingId: string, ordinal: number): CheckoutDraft {
  const own = withoutAdopted(draft, bookingId, ordinal);
  if (ordinal !== 0 || own.prefill?.bookingId !== bookingId) return own;
  return { ...own, prefill: null };
}

// ─── A save whose reply was lost ────────────────────────────────────────────

/**
 * A row as this caller's reader returns it once stored: without the private
 * answers the reader holds back from a guest, and without explicit nulls
 * (a cleared answer is dropped, not stored).
 */
export function readerViewOf(form: CheckoutForm, row: AnswerRow): Record<string, unknown> {
  const withheld = new Set(form.withheld_rider ? form.fields.filter((f) => f.sensitive === true).map((f) => f.key) : []);
  return Object.fromEntries(
    Object.entries(row).filter(([key, value]) => value !== null && value !== undefined && key !== SELF_KEY && !withheld.has(key)),
  );
}

/**
 * Is the rider the server holds at this place now one this page sent in a
 * save whose reply never came (`unconfirmed`, oldest first)? null: none of
 * them matches (someone else's rider). Judged on what the reader returns, so
 * two rows that differ only in private answers look the same here (as they
 * do to the server's rider_changed check). A row whose reader view is empty
 * (only private answers) is never matched: nothing tells it apart.
 *   row   the one that landed: every match is the same row (the latest).
 *   null  more than one matches and they differ (in private answers only):
 *         this page's own save landed, but which one is not known. The
 *         rider is never ticked on it (the server may hold the older private
 *         answer); the card is sent again, checked against the rider as it
 *         reads now.
 */
export function landedOwnSaveOf(
  form: CheckoutForm,
  ordinal: number,
  unconfirmed: readonly AnswerRow[],
): { row: AnswerRow | null } | null {
  // By rider id: a row sent with an id landed only as that rider; a row sent
  // without one (a new rider) took the id of the slot it filled, which the
  // page learns only now. So the answers are compared without the id, and a
  // sent id must be the stored one.
  const stored = expectedRow(form, ordinal);
  const storedId = participantIdOf(stored);
  const now = rowFingerprint(withoutId(stored));
  const matches = unconfirmed.filter((sent) => {
    const sentId = participantIdOf(sent);
    if (sentId !== null && sentId !== storedId) return false;
    // The marker is consumed, not echoed in the row. Matching answers alone
    // cannot prove a lost self-save landed: the reader must confirm its link.
    if (sent[SELF_KEY] === true && (!storedId || form.booker_participant_id !== storedId)) return false;
    const view = withoutId(readerViewOf(form, sent));
    return Object.keys(view).length > 0 && rowFingerprint(view) === now;
  });
  if (matches.length === 0) return null;
  const rows = new Set(matches.map((sent) => rowFingerprint(sent)));
  return { row: rows.size === 1 ? matches[matches.length - 1]! : null };
}

function withoutId(row: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const { [PARTICIPANT_ID_KEY]: _id, ...rest } = row;
  return rest;
}

/**
 * The rider the page saw at this place, for "did it change?": a slot nobody
 * is in yet reads the same whether the page read {} or the placeholder the
 * server padded the party with ({_pid}, no answer): nobody either way.
 */
export function riderSeen(form: CheckoutForm | undefined, ordinal: number): string {
  if (!form) return rowFingerprint({});
  const row = expectedRow(form, ordinal);
  return rowFingerprint(isPlaceholderRow(row) ? {} : row);
}

/**
 * A row the page sent, as the server now holds it: with the rider's id the
 * server gave it (a row sent without one took the id of the slot it
 * filled). The draft row that was sent takes the same id (adoptSavedIds).
 */
export function withStoredId(sent: AnswerRow, stored: Readonly<Record<string, unknown>>): AnswerRow {
  const id = participantIdOf(stored);
  const { [SELF_KEY]: _self, ...answers } = sent;
  return id && participantIdOf(sent) === null ? ({ ...answers, [PARTICIPANT_ID_KEY]: id } as AnswerRow) : answers;
}

/** landedOwnSaveOf's row: the one of this page's lost saves the server holds now, or null (none, or not known which). */
export function landedOwnSave(form: CheckoutForm, ordinal: number, unconfirmed: readonly AnswerRow[]): AnswerRow | null {
  return landedOwnSaveOf(form, ordinal, unconfirmed)?.row ?? null;
}

/**
 * What was sent, as the page builds it once the save is accepted (the
 * mark, and what "changed while it was saving" is judged against): the row
 * the page would send from the state it was sent from, with the reply
 * applied. Usually the sent row itself; an adopted rider's explicit nulls
 * (CheckoutDraft.adopted) go once it is no longer adopted, since the page
 * then leaves an empty private answer out.
 */
export function sentAsSaved(
  form: CheckoutForm,
  draft: CheckoutDraft,
  ordinal: number,
  row: unknown,
  outstanding: unknown,
): AnswerRow {
  const after = formAfterRiderSaved(form, ordinal, row, outstanding);
  return riderRowPayload(after, draftAfterRiderSaved(draft, form.booking_id, ordinal), ordinal);
}

/** The marks after an accepted save: this rider holds exactly what was sent. */
export function markSaved(saved: SavedRiders, slot: RiderSlot, sent: AnswerRow): SavedRiders {
  return { ...saved, [slot.key]: rowFingerprint(sent) };
}

/**
 * The marks once the guest has moved past these riders, or saved: each one
 * with nothing to send (an all-optional card left empty, nothing cleared,
 * nothing stored) and nothing missing is in as shown, so its chip is ticked
 * like any saved rider and a reopened checkout goes past it. Never before
 * the guest has been past it: a fresh checkout's empty riders are not
 * "saved" (the full page would skip its Riders step). A rider typed on
 * later is not (its mark no longer matches). The same object when nothing
 * changes.
 */
export function markNothingToSend(
  visits: readonly CheckoutVisit[],
  draft: CheckoutDraft,
  saved: SavedRiders,
  slots: readonly RiderSlot[],
): SavedRiders {
  let out = saved;
  for (const slot of slots) {
    if (riderIssue(visits, draft, slot) !== null || !riderHasNothingToSend(visits, draft, slot)) continue;
    const payload = riderPayloadOf(visits, draft, slot);
    if (!payload || out[slot.key] === rowFingerprint(payload)) continue;
    out = markSaved(out, slot, payload);
  }
  return out;
}

// ─── Reading the forms again ────────────────────────────────────────────────

function formIn(visits: readonly CheckoutVisit[], bookingId: string): CheckoutForm | undefined {
  for (const visit of visits) for (const form of visit.forms) if (form.booking_id === bookingId) return form;
  return undefined;
}

/**
 * This rider's card shows what the server holds now: the stored row in
 * place of what was typed, its cleared marks gone, and "use my details"
 * taken back if it was Rider 1. Never the other riders' typing.
 */
export function adoptStoredRider(draft: CheckoutDraft, form: CheckoutForm, ordinal: number): CheckoutDraft {
  const rows = [...(draft.rows[form.booking_id] ?? [])];
  while (rows.length <= ordinal) rows.push({});
  rows[ordinal] = { ...((form.participants[ordinal] ?? {}) as DraftRow) };
  const prefix = `r|${form.booking_id}|${ordinal}|`;
  const cleared = draft.cleared
    ? (Object.fromEntries(Object.entries(draft.cleared).filter(([k]) => !k.startsWith(prefix))) as Record<string, true>)
    : draft.cleared;
  return {
    ...draft,
    rows: { ...draft.rows, [form.booking_id]: rows },
    ...(cleared ? { cleared } : {}),
    prefill: ordinal === 0 && draft.prefill?.bookingId === form.booking_id ? null : draft.prefill,
  };
}

/**
 * After a read of the forms (the first load, a reload, or the read a
 * rider_changed refusal starts), with `draft` already merged (mergeDraft):
 *   * a rider whose typing the merge kept (an edited booking) keeps its mark
 *     while the server holds the same rider as before; when the server's
 *     rider CHANGED since the last read (another tab, the venue), or it is
 *     the rider a save was just refused for (`force`), the card shows the
 *     server's rider instead of the typing — never saved over it — and is
 *     listed in `changed` (when that changes what the card shows) so the
 *     page says so;
 *   * every other rider shows what the reader returned: in (ticked) when the
 *     server holds it as one of the riders storedRowsCounted counts (every
 *     rider once the reader calls the form complete; while riders are saved
 *     one at a time, each rider with answers), else not in.
 */
export function afterRead(input: {
  draft: CheckoutDraft;
  saved: SavedRiders;
  previousVisits: readonly CheckoutVisit[];
  visits: readonly CheckoutVisit[];
  force?: string | null;
  /**
   * Riders (RiderSlot.key) the server holds as one of this page's own saves
   * whose reply was lost (landedOwnSaveOf), with the row that landed: never
   * "changed elsewhere". The card keeps what the guest typed, and the rider
   * counts as saved while the card still holds that row. null: this page's
   * own, but not known which of its saves (they differ only in private
   * answers): the card keeps its typing, unticked, and is sent again.
   */
  own?: Readonly<Record<string, AnswerRow | null>>;
}): { draft: CheckoutDraft; saved: SavedRiders; changed: string[] } {
  let draft = input.draft;
  const saved: Record<string, string> = {};
  const changed: string[] = [];
  const counted = new Map<string, boolean[]>();
  for (const slot of riderSlots(input.visits)) {
    const form = formIn(input.visits, slot.bookingId);
    if (!form) continue;
    const before = formIn(input.previousVisits, slot.bookingId);
    const kept = draft.dirtyRows[form.booking_id] === true;
    const sameParty = !!before && before.party_size === form.party_size;
    const landed = input.own?.[slot.key];
    if (landed !== undefined && kept) {
      // The card keeps what the guest typed, under the id the server gave
      // the rider (a new rider's save took the id of the slot it filled).
      draft = withDraftRiderId(draft, form, slot.ordinal);
      if (landed) saved[slot.key] = rowFingerprint(withStoredId(landed, expectedRow(form, slot.ordinal)));
      continue;
    }
    const moved = !!before && riderSeen(before, slot.ordinal) !== riderSeen(form, slot.ordinal);
    if (kept && (moved || input.force === slot.key)) {
      // Said only when the guest had typed something on this card that the
      // server's rider now replaces (a rider they never touched — the card
      // still as last read — just shows the server's rider).
      const typed = rowFingerprint(riderRowPayload(form, draft, slot.ordinal));
      const lastRead = before
        ? rowFingerprint(riderRowPayload(form, adoptStoredRider(draft, before, slot.ordinal), slot.ordinal))
        : typed;
      // Adopted: the stored rider's private answers stay kept only while the
      // card still shows that rider (CheckoutDraft.adopted).
      draft = markAdopted(adoptStoredRider(draft, form, slot.ordinal), form, slot.ordinal);
      if (input.force === slot.key || typed !== lastRead) changed.push(slot.key);
    } else if (kept) {
      const mark = input.saved[slot.key];
      if (mark && sameParty) saved[slot.key] = mark;
      continue;
    }
    if (!counted.has(form.booking_id))
      counted.set(form.booking_id, storedRowsCounted(form.participants, form.party_size, form.complete));
    if (counted.get(form.booking_id)![slot.ordinal])
      saved[slot.key] = rowFingerprint(riderRowPayload(form, draft, slot.ordinal));
  }
  return { draft, saved, changed };
}

/** The draft row at this place given the stored rider's id, when it has none (the same object when nothing changes). */
function withDraftRiderId(draft: CheckoutDraft, form: CheckoutForm, ordinal: number): CheckoutDraft {
  const id = participantIdOf(expectedRow(form, ordinal));
  const rows = draft.rows[form.booking_id];
  const row = rows?.[ordinal];
  if (!id || !rows || !row || participantIdOf(row) !== null) return draft;
  const next = [...rows];
  next[ordinal] = { ...row, [PARTICIPANT_ID_KEY]: id } as DraftRow;
  return { ...draft, rows: { ...draft.rows, [form.booking_id]: next } };
}

// ─── The words ──────────────────────────────────────────────────────────────

/** "Rider 2" for this card. */
export function riderTitle(noun: GuestNoun, slot: Pick<RiderSlot, "number">): string {
  return `${nounTitle(noun)} ${slot.number}`;
}

/** The titles ("Rider 2") of these rider cards (by RiderSlot.key), in card order. */
export function riderTitlesOf(visits: readonly CheckoutVisit[], keys: readonly string[]): string[] {
  const out: string[] = [];
  for (const slot of riderSlots(visits)) {
    if (!keys.includes(slot.key)) continue;
    const found = slotForm(visits, slot);
    out.push(riderTitle(found?.visit.noun ?? { one: "guest", other: "guests" }, slot));
  }
  return out;
}

/** Said (politely, to a screen reader) as the checkout moves on from a rider it saved. */
export function savedAnnouncement(who: string): string {
  return `${who} saved.`;
}

/**
 * rider_changed: another save put a different rider here since the page
 * read it — most often the guest's own other window, sometimes the venue.
 */
export function riderChangedMessage(who: string): string {
  return `${who}'s details were just changed in another window or by the venue, so we've loaded what's saved now. Please check them.`;
}

/** A read of the forms found riders changed under the guest's typing. */
export function ridersChangedMessage(whos: readonly string[]): string {
  if (whos.length === 1) return riderChangedMessage(whos[0]!);
  return `The details of ${whos.slice(0, -1).join(", ")} and ${whos[whos.length - 1]} were just changed in another window or by the venue, so we've loaded what's saved now. Please check them.`;
}

/**
 * identity_changed: the guest changed the name of a rider whose details were
 * already saved, on a form with private questions. The server keeps each
 * rider's private answers with that rider, so it will not take a new name
 * for them without an answer: the card asks "Is this still {name}?" (P4a),
 * and these words go with it.
 */
export function identityChangedRiderMessage(who: string): string {
  return `You changed ${who}'s name after their details were saved. Please tell us whether it is still the same person.`;
}

/** invalid_participant_details, when the checkout's own rules find nothing to point at. */
export function invalidRiderMessage(who: string): string {
  return `Please check ${who}'s details: something the venue needs is missing or not in the form it expects. If everything looks right, please contact the venue.`;
}

/**
 * The network: nothing is known to be saved, the answers stay on the card.
 * `again`: the button that tries again ("Next", or "Continue" on the last
 * rider).
 */
export function offlineMessage(who: string, again = "Next"): string {
  return `We couldn't save ${who} — please check your connection. Your answers are still here; tap ${again} to try again.`;
}

/** The server answered with an error of its own (not the guest's connection): the answers stay on the card. */
export function serverErrorMessage(who: string, again = "Next"): string {
  return `We couldn't save ${who}'s details just now. Your answers are still here; tap ${again} to try again, or contact the venue if it keeps happening.`;
}

/** The network or the server, when the guest moved to another rider or step anyway (Back, a chip). */
export function offlineMovedMessage(who: string): string {
  return `We couldn't save ${who} just now. Their answers are kept on this page, and we'll try again before you continue.`;
}

/**
 * The guest changed the card while its save was on its way: the server
 * holds the answers as they were sent, so the page stays on the card (never
 * announcing "saved" over the change) until the change is saved too.
 */
export function changedWhileSavingMessage(who: string, again = "Next"): string {
  return `You changed ${who} while it was being saved. Tap ${again} to save your change.`;
}

/** Under a saved rider's title once it is edited again (in place of "Saved"). */
export function unsavedChangesNote(again = "Next"): string {
  return `Changes will be saved when you tap ${again}.`;
}

/**
 * The button that saves the rider on screen, named in the words above: Next
 * on every rider but the last; on the last, the full page's Continue, or in
 * the drawer "Save guest details" (its pager has no Next there; that button
 * saves the rider without paying, and is always shown while a rider is not
 * saved, whatever the payment button says: "Pay …", "Confirm booking"). A
 * stop found by the payment button itself names that button instead
 * (ParticipantDetails' payName).
 */
export const DRAWER_SAVE_BUTTON = "Save guest details";

export function retryButton(where: "page" | "drawer", last: boolean): string {
  return !last ? "Next" : where === "page" ? "Continue" : DRAWER_SAVE_BUTTON;
}

/**
 * Save guest details stopped on a rider (the drawer's pager): the words go on
 * that rider's card, which can be far above the button, so a short copy sits
 * beside the button too — the words' first sentence, and where the rest is
 * ("See Rider 2 above.").
 */
export function saveStopShort(message: string, who: string | null): string {
  const text = message.trim();
  const first = /^[\s\S]*?[.!?](?=\s|$)/.exec(text)?.[0] ?? text;
  const ended = /[.!?]$/.test(first) ? first : `${first}.`;
  return who ? `${ended} See ${who} above.` : ended;
}

/** On an adopted rider the guest changed (CheckoutDraft.adopted): why the private questions are asked again. */
export const ADOPTED_CHANGED_NOTE =
  "You've changed this rider's details, so the private answers saved for them are not kept. Please answer those questions again.";

/** Your details, when a restored checkout already holds riders (the contact is never kept). */
export function resumeSavedNotice(noun: GuestNoun, saved: number, total: number): string | null {
  if (saved <= 0) return null;
  const other = noun.other.trim() || "guests";
  const theirs = /s$/i.test(other) ? `${other}'` : `${other}'s`;
  return saved >= total
    ? `Your ${theirs} details are saved — enter your contact details to carry on.`
    : `The ${other} you've already filled in are saved — enter your contact details to carry on.`;
}

/** Continue on Your details went past Riders: every rider is in already. */
export function ridersSkippedNotice(noun: GuestNoun): string {
  const other = noun.other.trim() || "guests";
  return `Every one of your ${other} is saved already — you can still change them from Review & pay, or go back.`;
}

/**
 * Once Rider 1 was saved with the booker's details: why "Are you riding?"
 * (the page's Your details) or "use my details" (the drawer, above Rider 1)
 * has gone, and where Rider 1 is changed now.
 */
export function riderOneSavedNote(who: string, where: "page" | "drawer" = "page", nameLocked = false): string {
  // (P4a, review round) On a list whose saved names are guarded (a private
  // question), the name itself is not the booker's to change once saved:
  // say so here, and how a spelling mistake is put right, instead of
  // sending them to a question they can only answer by replacing themselves.
  const name = nameLocked
    ? ` ${who}'s name can't be changed once saved: if it's misspelt, book as it is and the venue can correct it.`
    : "";
  return where === "page"
    ? `${who} is saved with the details you gave. Changes here don't change ${who} — edit ${who} on the next step.${name}`
    : `${who} is saved with the details you gave. Changes to Your details don't change ${who} — edit ${who} here.${name}`;
}

/**
 * What a save that was not accepted means, in plain words, and what the page
 * does about it (every kind leaves the answers on the card and the rider
 * unticked):
 *   changed   rider_changed: nothing stored; that rider is read again and shown
 *   invalid   the server calls the row incomplete or wrong: say what is missing
 *   private   sensitive_answers_need_manager: never a guest's refusal; nothing
 *             is re-sent and no private answer is echoed
 *   lapsed    not_authorized / hold_expired: the hold ended (#1452's words);
 *             Pay and the steps stop
 *   offline   no answer at all (the network, or the page's time limit):
 *             retried on the next Next
 *   server    the server answered with an error (not the connection): the
 *             same as offline, in words that do not blame the connection
 *   refused   anything else; `reload`: the booking itself changed
 */
export type RiderSaveProblem =
  | { kind: "changed"; message: string }
  // focus (P4a): the card asks something to settle first — "Is this still
  // {name}?" (rename) or "Who is not coming?" (notcoming); the focus goes there.
  | { kind: "invalid"; message: string; focus?: "rename" | "notcoming" }
  | { kind: "private"; message: string }
  | { kind: "lapsed"; message: string }
  | { kind: "offline"; message: string }
  | { kind: "server"; message: string }
  | { kind: "refused"; message: string; reload: boolean };

/**
 * `network`: for "unknown", whether no answer came back at all (offline)
 * or the server answered with an error (server); unsaid, the network.
 * `again`: the button that tries again.
 */
export function riderSaveProblem(
  reason: unknown,
  who: string,
  noun: GuestNoun,
  opts: { network?: boolean; again?: string } = {},
): RiderSaveProblem {
  const again = opts.again ?? "Next";
  if (reason === "unknown" && opts.network === false) return { kind: "server", message: serverErrorMessage(who, again) };
  // The rider's id is not the one the server holds there (20261001110020):
  // this page's copy is out of date, exactly as rider_changed.
  if (isParticipantIdRefusal(reason)) return { kind: "changed", message: riderChangedMessage(who) };
  switch (reason) {
    case "rider_changed":
      return { kind: "changed", message: riderChangedMessage(who) };
    case "identity_changed":
      return { kind: "invalid", message: identityChangedRiderMessage(who), focus: "rename" };
    case "participant_pick_not_allowed":
    case "participant_self_twice":
      return { kind: "refused", message: `${who}'s details could not be saved. Please enter them again.`, reload: false };
    case "invalid_participant_details":
    case "empty_row":
      return { kind: "invalid", message: invalidRiderMessage(who) };
    // (P4a) A replaced rider's place can't be left empty on a list that asks
    // a required question: the new rider's answers go in the same save.
    case "replacement_required":
      return {
        kind: "invalid",
        message: `${who} can't be left empty: answer the questions for the new ${noun.one}, or press Undo to keep the saved one.`,
      };
    case "sensitive_answers_need_manager":
      return {
        kind: "private",
        message: `${who}'s private answers can't be saved from this page. Your answers are still here — please contact the venue to finish this booking.`,
      };
    case "not_authorized":
    case "hold_expired":
    case "invalid_token":
    case "already_confirmed":
      return { kind: "lapsed", message: LAPSE_MESSAGE };
    case "unknown":
    case undefined:
    case null:
      return { kind: "offline", message: offlineMessage(who, again) };
    case "invalid_position":
    case "rows_out_of_line":
    case "form_not_found":
      return {
        kind: "refused",
        message: `${who}'s details could not be saved: this booking just changed. Please check the ${noun.other} and try again.`,
        reload: true,
      };
    default:
      return { kind: "refused", message: `${who}'s details could not be saved. Please try again.`, reload: false };
  }
}

// ─── Leaving the rider on screen ────────────────────────────────────────────

/**
 * One rider's save (ParticipantDetailsHandle.saveRider); `who` is "Rider 2".
 * `edited`: the guest changed the card while the save was on its way, so
 * the card no longer shows what the server holds (the guest stays on it).
 * `rider`: the rider card (an index into riderSlots) the words are about,
 * when that is not the rider asked for (a save that waited for another one,
 * which read the forms again, stops with that save's words).
 */
export type RiderSaveResult =
  | { ok: true; sent: boolean; who: string; edited?: boolean }
  | {
      ok: false;
      problem: RiderSaveProblem | { kind: "incomplete"; message: string };
      who: string;
      edited?: boolean;
      rider?: number;
    };

/** Why the guest stays on a rider's card. */
export type LeaveProblem = RiderSaveProblem | { kind: "incomplete"; message: string };

/**
 * What leaving the rider on screen (Next, Back, another rider's chip) comes
 * to, the same on the full page and in the drawer's pager:
 *   go    the guest moves on. `announce`: said first ("Rider 1 saved.");
 *         `after`: said once moved (a save the network lost on the way).
 *   stay  the guest stays on the card. `problem` says why; null: nothing to
 *         say (the hold is over and Next is off, its words already shown).
 */
export type LeaveOutcome =
  | { go: true; announce: string | null; after: string | null }
  | { go: false; problem: LeaveProblem | null };

const MOVE_ON: LeaveOutcome = { go: true, announce: null, after: null };

/**
 * Before any save. The hold is over (or the forms are not there to save
 * from): nothing can be saved, Back and a chip still move, Next stays. A
 * rider still missing something is never sent (I1): Next says what is
 * missing; Back and a chip leave it as it is (never saved, never ticked).
 * null: save the rider first (leaveAfterSave judges the reply).
 */
export function leaveBeforeSave(
  how: "next" | "move",
  state: { holdOver: boolean; issue: string | null },
): LeaveOutcome | null {
  if (state.holdOver) return how === "move" ? MOVE_ON : { go: false, problem: null };
  if (state.issue) return how === "move" ? MOVE_ON : { go: false, problem: { kind: "incomplete", message: state.issue } };
  return null;
}

/**
 * The rider's save answered. Saved (or saved already): move on, saying so
 * when it was sent — unless the card changed while the save was on its way
 * (the server holds it as sent: stay, and say so, never "saved" over the
 * change). No answer, or the server's own error: Back and a chip still move
 * (the answers stay in the page, unticked, and the end of the step or Pay
 * tries again), Next stays. Any refusal: stay, in its words. `again`: the
 * button that tries again (retryButton).
 */
export function leaveAfterSave(result: RiderSaveResult, how: "next" | "move", again: string): LeaveOutcome {
  if (result.ok) {
    if (result.edited)
      return { go: false, problem: { kind: "refused", message: changedWhileSavingMessage(result.who, again), reload: false } };
    return { go: true, announce: result.sent ? savedAnnouncement(result.who) : null, after: null };
  }
  const problem = result.problem;
  if (problem.kind === "incomplete") return how === "move" ? MOVE_ON : { go: false, problem };
  if ((problem.kind === "offline" || problem.kind === "server") && how === "move" && !result.edited)
    return { go: true, announce: null, after: offlineMovedMessage(result.who) };
  return { go: false, problem };
}
